import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle, MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTableModule } from "@angular/material/table";
import { MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslatePipe } from "@ngx-translate/core";
import { ActionNeededDto } from "../../../../models/common/ActionNeededDto";
import { CommonDataService } from "../../../../services/common-data.service";
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { CostReviewParameters } from "../../../../models/common/CostReviewParameter";
import { JobDetails } from "../../common/job-details/job-details.component";
import { CommonFunctionsService } from "../../../../services/common-functions.service";
import { CONSTANTS } from "../../../../Constants/Constants";

export interface CostReviewReasonForExceptionDialogData {
  actionNeededCollection: ActionNeededDto[];
}

@Component({
  selector: 'app-cost-review-reason-for-exception-dialog',
  templateUrl: './cost-review-reason-for-exception.component.html',
  styleUrl: './cost-review-reason-for-exception.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    FormsModule,
    TranslatePipe,
    CloseOnEscapeDirective
  ]
})
export class CostReviewReasonForExceptionDialog implements OnInit {
    /**
     * Matches anything outside the XML 1.0 valid character set: tab, line feed, carriage return and
     * U+0020-U+D7FF / U+E000-U+FFFD. Deliberately declared without the `u` flag so it is evaluated per
     * UTF-16 code unit, mirroring desktop's char-by-char XmlConvert.IsXmlChar loop.
     */
    private static readonly INVALID_XML_CHARACTERS = /[^\u0009\u000A\u000D\u0020-\uD7FF\uE000-\uFFFD]/g;

    actionNeededCollection: ActionNeededDto[] = [];
    /** Reason-for-exception dropdown options (Fire-specific) */
    fireReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Automation-specific) */
    automationReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Electrical-specific) */
    electricalReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Mechanical-specific) */
    mechanicalReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Engineer Exception) */
    engineerReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Prefab Exception) */
    prefabReasonForExceptionOptions: string[] = [];
    /** Reason-for-exception dropdown options (Security Labor Variance) */
    securityReasonForLaborVarianceOptions: string[] = [];
    /** Reason-for-exception dropdown options (Security Exception) */
    securityReasonForExceptionOptions: string[] = [];

    private reviewParams: CostReviewParameters[] = [];

    constructor(
      @Inject(MAT_DIALOG_DATA) public data: CostReviewReasonForExceptionDialogData,
      private readonly dialogRef: MatDialogRef<CostReviewReasonForExceptionDialog>,
      private readonly commonDataService: CommonDataService,
      private readonly dialog: MatDialog,
      private readonly commonFunctionsService: CommonFunctionsService
    ) {}

    ngOnInit(): void {
      this.reviewParams = this.commonDataService.getReviewParameterResponse();
      this.actionNeededCollection = (this.data?.actionNeededCollection ?? []).map(item => ({ ...item }));
      this.loadReasonForExceptionOptions();
    }

    private readonly optionTypePrefixMap: Record<string, string> = {
      fireReasonForExceptionOptions: 'MARGIN_EXCEPTION_FIRE_',
      automationReasonForExceptionOptions: 'MARGIN_EXCEPTION_AUTOMATION_',
      electricalReasonForExceptionOptions: 'MARGIN_EXCEPTION_ELECTRICAL_',
      mechanicalReasonForExceptionOptions: 'MARGIN_EXCEPTION_MECHANICAL_',
      engineerReasonForExceptionOptions: 'ENGINEER_EXCEPTION_AUTOMATION_',
      prefabReasonForExceptionOptions: 'REASONFOREXCEPTION_PREFAB_',
      securityReasonForLaborVarianceOptions: 'REASON_FOR_LABOR_VARIANCE_',
      securityReasonForExceptionOptions: 'MARGIN_EXCEPTION_SECURITY_'
    }

    private loadReasonForExceptionOptions(): void {
      this.reviewParams.forEach(param => {
        for (const [optionType, prefix] of Object.entries(this.optionTypePrefixMap)) {
          if (param.parameterID.startsWith(prefix) && param.description?.trim()) {
            const key = optionType as keyof CostReviewReasonForExceptionDialog;
            (this[key] as string[]).push(param.description);
            break; // Exit the loop once a match is found to avoid adding to multiple arrays
          }
        }
      });
    }

    filterOptions(options: string[], value: string | null | undefined): string[] {
      if (!value || options.includes(value)) return options;
      const lower = value.toLowerCase();
      return options.filter(o => o.toLowerCase().includes(lower));
    }

    onOkClick(): void {
      this.actionNeededCollection.forEach(item => {
        item.actionTextValue = this.removeInvalidXmlCharacters(item.actionTextValue);
      });
      this.dialogRef.close({ isRequested: true, actionNeededCollection: this.actionNeededCollection });
    }

    /**
     * Strips characters that XML 1.0 does not allow, replacing each with a blank.
     *
     * The cost-review object is persisted as serialized XML, so a control character pasted into any
     * of this dialog's free-text fields (approver emails, reason-for-exception comboboxes) corrupts
     * the payload and fails the save. Desktop guards the same dialog via
     * CostReviewActionNeeded.xaml.cs -> CommonFunctions.PasteCommandHandler; cleaning on OK instead
     * of on paste additionally covers text entered by typing, drag-drop or autofill.
     *
     * Substituting a blank rather than deleting matches desktop's RemoveInvalidXMLCharcters and
     * keeps the string length unchanged, so the approver-email 50-character cap still behaves the same.
     */
    private removeInvalidXmlCharacters(value: string | undefined): string | undefined {
      if (!value) {
        return value;
      }
      return value.replace(CostReviewReasonForExceptionDialog.INVALID_XML_CHARACTERS, CONSTANTS.BLANK_SPACE);
    }

    onCancelClick(): void {
      this.dialogRef.close(null);
    }

    openJobDetailsDialog(): void {
      const dialogRef = this.dialog.open(JobDetails, {
                  width: '800px',
                  disableClose: true
      });
      dialogRef.afterClosed().subscribe(()=>{
        if (this.commonDataService.getBusiness() === CONSTANTS.BUSINESS_FIRE) {
          const taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
          if (!taskBasedLaborSettings){ 
            return;
          }
          this.commonDataService.jobDetailsExceptionVisible = this.commonFunctionsService.hasJobDetailsErrors(taskBasedLaborSettings);
        }
      })
    }
}